import React, { useState, useContext, useEffect } from "react";
import {
	EmployeeRecord,
	employeeTableHead,
	dropDownPagination,
} from "../../data";
import { Dropdown, Loader, Table } from "../../components";
import { useHistory } from "react-router-dom";
import { Link } from "react-router-dom";
import ApiCall from "../../utils/ApiCall";
import AuthContext from "../../contexts/AuthContext";
import { HRTIMESHEETVIEW, HRUSERVIEW } from "../../Constants";
import SweetAlert from "../../utils/SweetAlert";
import NoDataImg from "../../images/no_data.svg";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from "recharts";

const EmployeeDashboard = () => {
	const [pagination, setPagination] = useState(
		sessionStorage.getItem("emphr-dashboard-pagination-data")
			? JSON.parse(sessionStorage.getItem("emphr-dashboard-pagination-data"))
			: 10
	);
	const history = useHistory();
	const [isLoading, setIsLoading] = useState(true);
	const { authTokens } = useContext(AuthContext);
	const [employeeTableBody, setEmployeeTableBody] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const rowPerPage = 7;

	// New state for logged hours chart
	const [chartData, setChartData] = useState([]);
	const [isChartLoading, setIsChartLoading] = useState(false);
	const [selectedUserId, setSelectedUserId] = useState("");
	const [startDate, setStartDate] = useState("");
	const [endDate, setEndDate] = useState("");
	const [chartType, setChartType] = useState("line"); // "line" or "bar"

	useEffect(() => {
		fetchData();
		const savedPagination = sessionStorage.getItem(
			"emphr-dashboard-pagination-data"
		);
		if (savedPagination) {
			setPagination(JSON.parse(savedPagination));
		}
	}, [pagination]);

	const fetchData = async () => {
		try {
			const res = await ApiCall(
				`${HRTIMESHEETVIEW}?pg_r=${pagination}`,
				"GET",
				authTokens,
				null
			);
			setEmployeeTableBody(res.data);
			setIsLoading(false);
		} catch (error) {
			console.log("Error fetching data", error);
			setIsLoading(false);
		}
	};

	const fetchLoggedHours = async () => {
		if (!selectedUserId || !startDate || !endDate) {
			SweetAlert({ 
				title: "Please select user and date range", 
				icon: "warning" 
			});
			return;
		}

		if (new Date(startDate) > new Date(endDate)) {
			SweetAlert({ 
				title: "Start date cannot be after end date", 
				icon: "error" 
			});
			return;
		}

		setIsChartLoading(true);
		try {
			const res = await ApiCall(
				"timesheet/timesheetmanagerdashboard/", // Adjust this URL based on your API endpoint
				"POST",
				authTokens,
				{
					user_id: selectedUserId,
					start_date: startDate,
					end_date: endDate
				}
			);

			if (res.status === 200) {
				// Transform the data for the chart
				const loggedHoursByDate = res.data.logged_hours_by_date;
				const transformedData = Object.entries(loggedHoursByDate).map(([date, hours]) => ({
					date: new Date(date).toLocaleDateString('en-US', { 
						month: 'short', 
						day: 'numeric' 
					}),
					fullDate: date,
					hours: parseFloat(hours)
				})).sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate));

				setChartData(transformedData);
			} else {
				SweetAlert({ 
					title: res.data.error || "Error fetching data", 
					icon: "error" 
				});
			}
		} catch (error) {
			console.log("Error fetching logged hours", error);
			SweetAlert({ 
				title: "Error fetching logged hours data", 
				icon: "error" 
			});
		}
		setIsChartLoading(false);
	};

	const handlePageChange = (pageNumber) => {
		setCurrentPage(pageNumber);
	};

	const paginatedData = (e) => {
		e.preventDefault();
		setPagination(e.target.value);
		const pageValue = e.target.value;
		sessionStorage.setItem(
			"emphr-dashboard-pagination-data",
			JSON.stringify(pageValue)
		);
	};

	const handleEditRow = async (rowIndex) => {
		const actualIndex = (currentPage - 1) * rowPerPage + rowIndex;
		const id = employeeTableBody[actualIndex].id;
		const name = employeeTableBody[actualIndex].name;
		history.push({
			state: {
				userTimesheetEditId: id,
			},
		});

		try {
			const res = await ApiCall(
				`${HRUSERVIEW}?id=${id}`,
				"GET",
				authTokens,
				null
			);

			history.push({
				pathname: "/timesheet/employee-timesheet",
				state: {
					userTimesheetEdit: res.data.user,
					userTimesheetEditId: id,
					userTimesheetName: name,
				},
			});

			if (res.status == 400) {
				SweetAlert({ title: res.data, icon: "error" });
			}
		} catch (error) {
			console.log("While Requesting data", error);
		}
	};

	// Custom tooltip for the chart
	const CustomTooltip = ({ active, payload, label }) => {
		if (active && payload && payload.length) {
			return (
				<div className="bg-white p-3 border border-gray-300 rounded shadow-lg">
					<p className="font-medium">{`Date: ${label}`}</p>
					<p className="text-blue-600">
						{`Hours Logged: ${payload[0].value}`}
					</p>
				</div>
			);
		}
		return null;
	};

	return (
		<div className="w-auto my-6 mx-6 py-4 px-2">
			{isLoading ? (
				<div className="flex justify-center items-center w-full h-[60vh]">
					<Loader />
				</div>
			) : (
				<div className="mt-10">
					{/* Logged Hours Dashboard */}
					<div className="bg-white rounded-lg shadow-md p-6 mb-8">
						<h2 className="text-2xl font-bold mb-6 text-gray-800">Employee Logged Hours Analytics</h2>
						
						{/* Input Controls */}
						<div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">
									Select Employee
								</label>
								<select
									value={selectedUserId}
									onChange={(e) => setSelectedUserId(e.target.value)}
									className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
								>
									<option value="">Choose Employee</option>
									{employeeTableBody.map((employee) => (
										<option key={employee.id} value={employee.id}>
											{employee.name}
										</option>
									))}
								</select>
							</div>
							
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">
									Start Date
								</label>
								<input
									type="date"
									value={startDate}
									onChange={(e) => setStartDate(e.target.value)}
									className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
								/>
							</div>
							
							<div>
								<label className="block text-sm font-medium text-gray-700 mb-2">
									End Date
								</label>
								<input
									type="date"
									value={endDate}
									onChange={(e) => setEndDate(e.target.value)}
									className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
								/>
							</div>
							
							<div className="flex flex-col justify-end">
								<div className="flex gap-2 mb-2">
									<button
										onClick={() => setChartType("line")}
										className={`px-3 py-1 text-xs rounded ${
											chartType === "line" 
												? "bg-blue-500 text-white" 
												: "bg-gray-200 text-gray-700"
										}`}
									>
										Line
									</button>
									<button
										onClick={() => setChartType("bar")}
										className={`px-3 py-1 text-xs rounded ${
											chartType === "bar" 
												? "bg-blue-500 text-white" 
												: "bg-gray-200 text-gray-700"
										}`}
									>
										Bar
									</button>
								</div>
								<button
									onClick={fetchLoggedHours}
									disabled={isChartLoading}
									className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
								>
									{isChartLoading ? "Loading..." : "Generate Chart"}
								</button>
							</div>
						</div>

						{/* Chart Display */}
						{isChartLoading ? (
							<div className="flex justify-center items-center h-64">
								<Loader />
							</div>
						) : chartData.length > 0 ? (
							<div className="h-80 w-full">
								<ResponsiveContainer width="100%" height="100%">
									{chartType === "line" ? (
										<LineChart data={chartData}>
											<CartesianGrid strokeDasharray="3 3" />
											<XAxis 
												dataKey="date"
												tick={{ fontSize: 12 }}
											/>
											<YAxis 
												tick={{ fontSize: 12 }}
												label={{ value: 'Hours', angle: -90, position: 'insideLeft' }}
											/>
											<Tooltip content={<CustomTooltip />} />
											<Legend />
											<Line 
												type="monotone" 
												dataKey="hours" 
												stroke="#3B82F6" 
												strokeWidth={3}
												dot={{ fill: "#3B82F6", strokeWidth: 2, r: 4 }}
												activeDot={{ r: 6, stroke: "#3B82F6", strokeWidth: 2 }}
												name="Hours Logged"
											/>
										</LineChart>
									) : (
										<BarChart data={chartData}>
											<CartesianGrid strokeDasharray="3 3" />
											<XAxis 
												dataKey="date"
												tick={{ fontSize: 12 }}
											/>
											<YAxis 
												tick={{ fontSize: 12 }}
												label={{ value: 'Hours', angle: -90, position: 'insideLeft' }}
											/>
											<Tooltip content={<CustomTooltip />} />
											<Legend />
											<Bar 
												dataKey="hours" 
												fill="#3B82F6"
												name="Hours Logged"
												radius={[4, 4, 0, 0]}
											/>
										</BarChart>
									)}
								</ResponsiveContainer>
							</div>
						) : selectedUserId && startDate && endDate ? (
							<div className="flex flex-col items-center justify-center h-64 text-gray-500">
								<img className="w-32 mb-4" src={NoDataImg} alt="No data" />
								<p>No logged hours data found for the selected criteria</p>
							</div>
						) : (
							<div className="flex flex-col items-center justify-center h-64 text-gray-400">
								{/* <div className="text-4xl mb-4">📊</div> */}
								<p>Select employee and date range to view logged hours chart</p>
							</div>
						)}

						{/* Summary Stats */}
						{chartData.length > 0 && (
							<div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-4">
								<div className="bg-blue-50 p-4 rounded-lg">
									<div className="text-sm text-blue-600 font-medium">Total Hours</div>
									<div className="text-2xl font-bold text-blue-800">
										{chartData.reduce((sum, item) => sum + item.hours, 0).toFixed(1)}
									</div>
								</div>
								<div className="bg-green-50 p-4 rounded-lg">
									<div className="text-sm text-green-600 font-medium">Average Daily Hours</div>
									<div className="text-2xl font-bold text-green-800">
										{(chartData.reduce((sum, item) => sum + item.hours, 0) / chartData.length).toFixed(1)}
									</div>
								</div>
								<div className="bg-purple-50 p-4 rounded-lg">
									<div className="text-sm text-purple-600 font-medium">Max Daily Hours</div>
									<div className="text-2xl font-bold text-purple-800">
										{Math.max(...chartData.map(item => item.hours)).toFixed(1)}
									</div>
								</div>
								<div className="bg-orange-50 p-4 rounded-lg">
									<div className="text-sm text-orange-600 font-medium">Days Tracked</div>
									<div className="text-2xl font-bold text-orange-800">
										{chartData.length}
									</div>
								</div>
							</div>
						)}
					</div>

					{/* Existing Employee Table */}
					{employeeTableBody.length > 0 ? (
						<>
							<div className="w-full flex justify-end items-end pr-10">
								<Dropdown
									style="px-4"
									value={pagination}
									onChange={paginatedData}
									options={dropDownPagination}
								/>
							</div>
							<Table
								tableStyle="border-spacing-y-[10px] border-separate -mt-4"
								bodyStyle="bg-accent box-shadow"
								header={employeeTableHead}
								body={employeeTableBody.map((row) => [
									row.id,
									row.name,
									row.email,
									row.role,
								])}
								bodyTdStyle="table-body-td intro-x"
								headTdStyle="table-body-td intro-x"
								btnTitle={<Link to="/timesheet/employee-timesheet">Edit</Link>}
								paginationNeed={true}
								btnstyle="intro-x"
								currentPage={currentPage}
								paginationBody={employeeTableBody}
								rowPerPage={rowPerPage}
								onPageChange={handlePageChange}
								onClick={handleEditRow}
							/>
						</>
					) : (
						<h3 className="text-center w-full flex flex-col justify-center items-center intro-x my-4">
							<img className="w-72 text-center" src={NoDataImg} />
							<p className="font-bold">No data</p>
						</h3>
					)}
				</div>
			)}
		</div>
	);
};

export default EmployeeDashboard;